import convert from 'convert';
import byteSize from 'byte-size';

const units = [
  { name: 'y', value: 365 * 24 * 60 * 60 * 1000 }, // years
  { name: 'w', value: 7 * 24 * 60 * 60 * 1000 },   // weeks
  { name: 'd', value: 24 * 60 * 60 * 1000 },       // days
  { name: 'h', value: 60 * 60 * 1000 },            // hours
  { name: 'm', value: 60 * 1000 },                 // minutes
  { name: 's', value: 1000 },                      // seconds
  { name: 'ms', value: 1 }                         // milliseconds
];

/**
 * Converts Unix timestamps (in seconds) to Prometheus range-vector and offset strings
 * that express the time range as an offset from the current time.
 * 
 * @param {number} startTimestamp - Start timestamp in Unix seconds
 * @param {number} endTimestamp - End timestamp in Unix seconds
 * @returns {Object} Object containing:
 *   - rangeVector: Prometheus range-vector string (e.g., "[5m]" or "[1h30m]"), or null if duration is 0
 *   - offset: Offset string showing how far back start timestamp is (e.g., "offset 1w"), or null if offset is 0
 * 
 * @example
 * // For a 5-minute range ending now
 * unixTimestampsToPrometheusRange(1640995200, 1640995500)
 * // Returns: { rangeVector: "[5m:]", offset: "offset 5m" }
 * 
 * @example
 * // For a 1 hour 30 minute range ending now
 * unixTimestampsToPrometheusRange(1640991600, 1640997000)
 * // Returns: { rangeVector: "[1h30m:]", offset: "offset 1h30m" }
 * 
 * @example
 * // For zero duration (start equals end)
 * unixTimestampsToPrometheusRange(1640995200, 1640995200)
 * // Returns: { rangeVector: null, offset: "offset 5m" }
 */
export const unixTimestampsToPrometheusRange = (startTimestamp, endTimestamp) => {
  if (!startTimestamp || !endTimestamp) {
    throw new Error('Both startTimestamp and endTimestamp are required');
  }

  if (typeof startTimestamp !== 'number' || typeof endTimestamp !== 'number') {
    throw new Error('Both timestamps must be numbers');
  }

  if (startTimestamp > endTimestamp) {
    throw new Error('startTimestamp must be less than endTimestamp');
  }

  const now = Math.floor(Date.now() / 1000); // Convert to seconds to match input format
  const durationMs = (endTimestamp - startTimestamp) * 1000; // Convert to milliseconds for duration calculation
  const offsetMs = (now - endTimestamp) * 1000; // Convert to milliseconds for offset calculation

  const rangeVector = formatDurationToPrometheusRange(durationMs);
  const offsetString = formatDurationToOffsetString(offsetMs);

  return {
    rangeVector,
    offset: offsetString
  };
};

/**
 * Formats a duration in milliseconds to a Prometheus range-vector string
 * using the most appropriate time units (y, w, d, h, m, s, ms)
 * 
 * @param {number} durationMs - Duration in milliseconds
 * @returns {string|null} Prometheus range-vector string, or null if duration is 0
 * 
 * @example
 * formatDurationToPrometheusRange(300000) // Returns: "[5m]"
 * formatDurationToPrometheusRange(3661000) // Returns: "[1h1m1s]"
 * formatDurationToPrometheusRange(0) // Returns: null
 */
const formatDurationToPrometheusRange = (durationMs) => {
  if (durationMs === 0) {
    return null;
  }

  let remainingMs = durationMs;
  const parts = [];

  // Process units from largest to smallest
  for (const unit of units) {
    if (remainingMs >= unit.value) {
      const count = Math.floor(remainingMs / unit.value);
      if (count > 0) {
        parts.push(`${count}${unit.name}`);
        remainingMs -= count * unit.value;
      }
    }
  }

  return `[${parts.join('')}:]`;
};

/**
 * Formats a duration in milliseconds to a Prometheus offset string
 * using the most appropriate time units (y, w, d, h, m, s, ms)
 * 
 * @param {number} durationMs - Duration in milliseconds
 * @returns {string|null} Prometheus offset string, or null if duration is 0
 * 
 * @example
 * formatDurationToOffsetString(300000) // Returns: "offset 5m"
 * formatDurationToOffsetString(3661000) // Returns: "offset 1h1m1s"
 * formatDurationToOffsetString(0) // Returns: null
 */
const formatDurationToOffsetString = (durationMs) => {
  if (durationMs === 0) {
    return null;
  }

  let remainingMs = durationMs;
  const parts = [];

  // Process units from largest to smallest
  for (const unit of units) {
    if (remainingMs >= unit.value) {
      const count = Math.floor(remainingMs / unit.value);
      if (count > 0) {
        parts.push(`${count}${unit.name}`);
        remainingMs -= count * unit.value;
      }
    }
  }

  return `offset ${parts.join('')}`;
};

const MEM_UNIT_MAP = {
  'kb': 'KiB',
  'mb': 'MiB',
  'gb': 'GiB',
  'tb': 'TiB',
}

const MEM_UNIT_DISPLAY_MAP = {
  'KiB': 'Ki',
  'MiB': 'Mi',
  'GiB': 'Gi',
  'TiB': 'Ti',
}

export const valueFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

// convert yields objects and strings - determine what type the value is and formats it
const unitConversionHelper = (unitConversion, showUnits = false, units = "") => {
  if (!unitConversion.quantity) {
    if (units?.length && showUnits) {
      return `${valueFormatter.format(unitConversion)} ${units}`;
    }
    return valueFormatter.format(unitConversion)
  }

  if (showUnits) {
    return `${valueFormatter.format(unitConversion.quantity)} ${unitConversion.unit}`;
  }
  return `${valueFormatter.format(unitConversion.quantity)}`
}

// TODO - Test b to KiB/GiB conversions - unit tests needed
// TODO - Test B to GB conversions - unit tests needed

// TODO - Determine why values are sometimes objects and sometimes string coming from convert()
// TODO - Unit Tests for GiB conversions
export const formatUnitRatioForMemory = (lowVal, highVal) => {
  // Convert highest value to best units
  const suggestedHighVal = convert(highVal, 'B').to('best');

  // Convert Units to Memory Appropriate Units
  const highUnits = MEM_UNIT_MAP[suggestedHighVal.unit.toLowerCase()] || 'KiB';

  // Reconvert Values using the correct units
  const newHighVal = convert(suggestedHighVal.quantity, suggestedHighVal.unit).to(highUnits);
  const newLowVal = convert(lowVal, 'B').to(highUnits);

  // Reformat the units if there's a mapping available
  const reformattedUnits = MEM_UNIT_DISPLAY_MAP[highUnits] ? MEM_UNIT_DISPLAY_MAP[highUnits] : highUnits;

  const low = unitConversionHelper(newLowVal, false, reformattedUnits);
  const high = unitConversionHelper(newHighVal, true, reformattedUnits);

  return `${low}/${high}`;
}

// TODO - Unit Tests for bytes to GB conversions
export const formatUnitRatioForStorage = (lowVal, highVal) => {
  // Get Units of Highest Value
  const highValFormatted = byteSize(highVal);
  const units = highValFormatted.unit.toLowerCase();

  // Convert Lowest Value from bytes to the Highest Value's Units
  const newLowVal = convert(lowVal, 'bytes').to(units.toUpperCase());
  const low = unitConversionHelper(newLowVal);
  const high = `${highValFormatted.value} ${highValFormatted.unit}`;

  return `${low}/${high}`;
}

// TODO - Unit Tests for GiB conversions
export const formatMemoryUnitsAsGi = (memVal) => {
  const newMemVal = convert(memVal, 'bytes').to('GiB');

  return unitConversionHelper(newMemVal);
}

// TODO - Unit Tests for GB conversions
export const formatMemoryUnitsAsGB = (memVal) => {
  const memValFormatted = byteSize(memVal);

  return `${memValFormatted.value} ${memValFormatted.unit}`;
}

export const combineRangeAndOffset = (rangeVector, offset) => {
  if (offset === null && rangeVector === null) {
    return '';
  }
  if (offset === null) {
    return `${rangeVector}`;
  }
  if (rangeVector === null) {
    return ` ${offset}`;
  }

  return `${rangeVector} ${offset}`;
}
